import { useTranslation } from 'next-i18next'
import { useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import DecimalPlacementMessage from '~/bootcamp/Activities/components/DecimalPlacementMessage'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import InputSingleDigitColumns from '~/bootcamp/Activities/components/InputSingleDigitColumns'
import InputSingleDigitColumnsWithComma from '~/bootcamp/Activities/components/inputSingleDigitColumnWIthComma'
import MultiplicationActivityWrapper from '~/bootcamp/Activities/Multiplication/components/MultiplicationActivityWrapper'
import Plus from '~/bootcamp/Activities/components/Plus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import { range } from '~/utils/helpers'
import { hocOnEnterPress, isDecimalSumComplete } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import { getRanges2 } from './constants'
import { evaluateDecimalAnswer, generateTerms, multiplyDecimals } from './helpers'
import { LongMultiStateKey, LongMultiTwoDigitState, TaskHistory } from './types'
import InputRowWrapper from './components/InputRowWrapper'
import ExpressionWithComma from './components/ExpressionWithComma'
import CarriedNumbersExpression from './components/CarriedNumbersExpression'
import CarriedNumbersAdditionRows from './components/CarriedNumbersAdditionRows'

const initialState: LongMultiTwoDigitState = {
  [LongMultiStateKey.ANSWER]: {},
  [LongMultiStateKey.ADDITON_ROW_1]: {},
  [LongMultiStateKey.ADDITON_ROW_2]: {},
  [LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_1]: {},
  [LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_2]: {},
  [LongMultiStateKey.CARRIED_NUMBERS_ADDITION]: {},
}

/* to deal with the floating point arihmatic issue decimal.js-light being used as temopary solutions */
export default function LongMultiDecimalTwoDigits({
  difficultyLevel,
  onNextQuestionClick,
  answerState,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { locale } = useRouter()
  const [numbersState, setNumbersState] = useState<LongMultiTwoDigitState>(initialState)
  const [clickedDecimalIndex, setClickedDecimalIndex] = useState<number>()
  const [isDecimalMisplaced, setIsDecimalMisplaced] = useState(false)
  const [taskHistory, setTaskHistory] = useState<TaskHistory[]>([])
  const [inputKey, setInputKey] = useState<string>(uuid())
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  const {
    multiplicandDigitMax,
    multiplicandDigitMin,
    multiplierDigitMin,
    multiplierDigitMax,
    multiplierLen,
    multiplicandLen,
  } = useMemo(() => {
    return getRanges2('LONG_MULTI_DECIMAL_TWO_DIGITS', difficultyLevel)
  }, [difficultyLevel])

  const [multiplier, multiplicand, maxAnswerLen] = useMemo(() => {
    const taskHistoryMultipliers = taskHistory.map((elem) => elem.multiplier)
    const taskHistoryMultiplicands = taskHistory.map((elem) => elem.multiplicand)

    const { multiplicand: _multiplicand, multiplier: _multiplier } = generateTerms(
      { multiplicandDigitMin, multiplicandDigitMax, multiplicandLen, taskHistoryMultiplicands },
      { multiplierDigitMin, multiplierDigitMax, multiplierLen, taskHistoryMultipliers },
      difficultyLevel,
    )

    // const answerString = multiplyDecimals(_multiplier, _multiplicand).toString()
    const _maxAnswerLen = multiplierLen + multiplicandLen // decimal should not have a field

    setNumbersState(getInitialState(_maxAnswerLen, multiplicandLen - 1))

    return [String(_multiplier), String(_multiplicand), _maxAnswerLen]
  }, [
    multiplicandDigitMax,
    multiplicandDigitMin,
    multiplierDigitMin,
    multiplierDigitMax,
    multiplierLen,
    multiplicandLen,
    taskHistory,
    difficultyLevel,
  ])

  // Only the two factors — the rest of numbersState is the worked answer. Order mirrors the JSX (it
  // swaps multiplicand and multiplier); the separator stays ".", Norwegian re-paints "," downstream.
  useEmitInstance(
    onInstanceChange,
    multiplier && multiplicand ? `${multiplicand} \\cdot ${multiplier} = \\square` : null,
  )

  const onDecimalClick = (decimalIndex?: number): void => {
    setClickedDecimalIndex(decimalIndex)
    // Moving the sign is the pupil's retry, so clear the previous verdict instead of leaving the
    // row marked wrong until they hit the clear button.
    setIsDecimalMisplaced(false)
    if (answerState !== null) onAnswerStateChange(null)
  }

  const handleOnCheckAnswerClick = (): void => {
    const trueAnswer = multiplyDecimals(parseFloat(multiplier), parseFloat(multiplicand))
    // An unused leftmost box is a leading zero, not a missing digit — without that the sign's
    // column would be read against a shorter number than the pupil is looking at.
    const digits = Object.values(numbersState[LongMultiStateKey.ANSWER]).map((item) => item || '0')
    const verdict = evaluateDecimalAnswer({
      decimalIndex: clickedDecimalIndex,
      digits,
      trueAnswer,
    })

    setIsDecimalMisplaced(verdict.isDecimalMisplaced)
    onAnswerStateChange(verdict.isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [{ multiplier: Number(multiplier), multiplicand: Number(multiplicand) }, ...prevState].slice(0, 3)
    })

    setClickedDecimalIndex(undefined)
    setIsDecimalMisplaced(false)
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (isFormValid()) {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  const handleOnRemoveButtonClick = (): void => {
    setNumbersState(getInitialState(maxAnswerLen, multiplicandLen - 1))
    setClickedDecimalIndex(undefined)
    setIsDecimalMisplaced(false)
  }

  const handleOnChange = (value: string, name: string): void => {
    const key = name.split(':')[0] as keyof LongMultiTwoDigitState

    setNumbersState((prevState) => ({
      ...prevState,
      [key]: { ...prevState[key], [name]: value },
    }))
  }

  const commonGridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${maxAnswerLen + 1}, minmax(0, 1fr))`,
  }

  const inputGridStyle = {
    gridColumnStart: 2,
    gridColumnEnd: maxAnswerLen + 2,
  }

  // The decimal sign is part of the answer, so the answer cannot be delivered before it is placed.
  const isFormValid = (): boolean => {
    const answer = numbersState[LongMultiStateKey.ANSWER]
    return clickedDecimalIndex !== undefined && Object.keys(answer).some((key: string) => answer[key] !== '')
  }

  return (
    <ActivityWrapper>
      <MultiplicationActivityWrapper className="flex-col space-y-1">
        <CarriedNumbersExpression
          carriedNumbers={numbersState[LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_1]}
          gridCols={locale === 'no' ? maxAnswerLen + 1 : multiplicandLen}
          onCarriedNumbersChange={handleOnChange}
        />
        <CarriedNumbersExpression
          carriedNumbers={numbersState[LongMultiStateKey.CARRIED_NUMBERS_MULT_ROW_2]}
          gridCols={locale === 'no' ? maxAnswerLen + 1 : multiplicandLen}
          onCarriedNumbersChange={handleOnChange}
        />
        <ExpressionWithComma maxAnswerLen={maxAnswerLen} multiplicand={multiplier} multiplier={multiplicand} />
        <Divider />

        <CarriedNumbersAdditionRows
          carriedNumbers={numbersState[LongMultiStateKey.CARRIED_NUMBERS_ADDITION]}
          maxAnswerLen={maxAnswerLen}
          onCarriedNumbersChange={handleOnChange}
        />
        <InputRowWrapper className="self-end">
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertTerm')}
              autoFocus
              id={`LongMultiDecimalTwoDigits:${inputKey}-first`}
              onChange={handleOnChange}
              onEnter={onEnterPress}
              ref={inputRef}
              style={inputGridStyle}
              values={numbersState[LongMultiStateKey.ADDITON_ROW_1]}
            />
          </div>
        </InputRowWrapper>
        <InputRowWrapper className="justify-between w-full">
          <Plus />
          <div style={commonGridStyle}>
            <InputSingleDigitColumns
              aria-label={t('ariaLabelButtons.insertTerm')}
              id={`LongMultiDecimalTwoDigits:${inputKey}-second`}
              onChange={handleOnChange}
              onEnter={onEnterPress}
              style={inputGridStyle}
              values={numbersState[LongMultiStateKey.ADDITON_ROW_2]}
            />
          </div>
        </InputRowWrapper>
        <Divider />

        <InputRowWrapper className="justify-between w-full">
          <Equals />
          <InputSingleDigitColumnsWithComma
            aria-label={t('ariaLabelButtons.insertSum')}
            clickedDecimalIndex={clickedDecimalIndex}
            id={`LongMultiDecimalTwoDigits:${inputKey}-answer`}
            isDecimalError={isDecimalMisplaced}
            isError={answerState === false}
            onChange={handleOnChange}
            onDecimalClick={onDecimalClick}
            onEnter={onEnterPress}
            style={inputGridStyle}
            values={numbersState[LongMultiStateKey.ANSWER]}
          />
        </InputRowWrapper>

        {/* The decimal signs hang below the answer row, so the closing rule steps down out of
            their way while they are on screen. */}
        <Divider className={isDecimalSumComplete(numbersState[LongMultiStateKey.ANSWER]) ? '!mt-5' : ''} />
        <Divider />
        {/* Below the closing rule rather than inside the calculation, so the instruction does not
            read as part of the problem. */}
        <DecimalPlacementMessage
          answerValues={numbersState[LongMultiStateKey.ANSWER]}
          decimalIndex={clickedDecimalIndex}
          isDecimalError={isDecimalMisplaced}
        />
      </MultiplicationActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: isFormValid(),
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: handleOnRemoveButtonClick,
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function getInitialState(digits: number, carriedNumbersCount: number): LongMultiTwoDigitState {
  const {
    ANSWER,
    ADDITON_ROW_1,
    ADDITON_ROW_2,
    CARRIED_NUMBERS_MULT_ROW_1,
    CARRIED_NUMBERS_MULT_ROW_2,
    CARRIED_NUMBERS_ADDITION,
  } = LongMultiStateKey

  return {
    [ANSWER]: getEmptyObj(digits, ANSWER),
    [ADDITON_ROW_1]: getEmptyObj(digits, ADDITON_ROW_1),
    [ADDITON_ROW_2]: getEmptyObj(digits, ADDITON_ROW_2),
    [CARRIED_NUMBERS_MULT_ROW_1]: getEmptyObj(carriedNumbersCount, CARRIED_NUMBERS_MULT_ROW_1),
    [CARRIED_NUMBERS_MULT_ROW_2]: getEmptyObj(carriedNumbersCount, CARRIED_NUMBERS_MULT_ROW_2),
    [CARRIED_NUMBERS_ADDITION]: getEmptyObj(digits - 1, CARRIED_NUMBERS_ADDITION),
  }
}

function getEmptyObj(count: number, name: LongMultiStateKey): Record<string, string> {
  return range(count).reduce(
    (acc, currValue) => ({
      ...acc,
      [`${name}:${currValue + 1}`]: '',
    }),
    {},
  )
}
